import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, 
  Shield, 
  Zap, 
  DollarSign, 
  Receipt, 
  CheckCircle, 
  Users, 
  Globe,
  Bot,
  CreditCard,
  TrendingUp,
  Lock,
  LogIn
} from 'lucide-react';
import { ModernCard } from '../components/ModernCards';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Bot className="h-8 w-8 text-green-600" />,
      title: "AI-Powered Receipt Processing",
      description: "Upload receipts and let our Fetch.ai agents automatically extract expense details, eliminating manual data entry."
    },
    {
      icon: <Globe className="h-8 w-8 text-green-600" />,
      title: "Real-time Blockchain Monitoring",
      description: "Monitor Solana wallets via Helius WebSockets to automatically create expenses from PYUSD and SOL transactions."
    },
    {
      icon: <Shield className="h-8 w-8 text-green-600" />,
      title: "Automated Approval Workflows",
      description: "Dynamic, multi-step approval processes with configurable rules and parallel processing capabilities."
    },
    {
      icon: <DollarSign className="h-8 w-8 text-green-600" />,
      title: "Crypto Reimbursements",
      description: "Automatically process PYUSD reimbursements through Solana blockchain when expenses are approved."
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-green-600" />,
      title: "Real-time Price Conversion",
      description: "Integrate Pyth Network price feeds to convert crypto amounts to USD for accurate financial reporting."
    },
    {
      icon: <Lock className="h-8 w-8 text-green-600" />,
      title: "Enterprise Security",
      description: "Bank-grade security with encrypted data storage, secure API endpoints, and compliance-ready audit trails."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 gradient-hero rounded-lg flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900 font-display">Expenso</span>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="btn-primary flex items-center space-x-2"
            >
              <LogIn className="h-4 w-4" />
              <span>Go to App</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 font-display animate-fade-in-up">
              The Future of
              <span className="bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-transparent animate-gradient"> Crypto Expense Management</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
              Automate your crypto-native business expenses with AI-powered receipt processing, 
              real-time blockchain monitoring, and instant PYUSD reimbursements.
            </p>
            <div className="flex justify-center animate-fade-in-up animation-delay-400">
              <button
                onClick={() => navigate('/login')}
                className="btn-primary text-lg px-8 py-3 flex items-center justify-center space-x-2 hover:scale-105 transition-transform duration-200"
              >
                <span>Go to App</span>
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-display animate-fade-in-up">
              Powerful Features for Modern Businesses
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
              Everything you need to manage expenses in the crypto economy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <ModernCard key={index} className={`p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-2 animate-fade-in-up`} style={{ animationDelay: `${index * 100 + 400}ms` }}>
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4 hover:scale-110 transition-transform duration-200">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </ModernCard>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-display animate-fade-in-up">
              How It Works
            </h2>
            <p className="text-xl text-green-100 max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
              Simple, automated, and secure expense management in three steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center animate-fade-in-up animation-delay-300">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 hover:scale-110 transition-transform duration-200">
                <Receipt className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">1. Upload Receipt</h3>
              <p className="text-green-100">
                Upload your receipt or let our system automatically detect blockchain transactions
              </p>
            </div>

            <div className="text-center animate-fade-in-up animation-delay-400">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 hover:scale-110 transition-transform duration-200">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">2. Automated Review</h3>
              <p className="text-green-100">
                AI extracts details and routes through your configured approval workflow
              </p>
            </div>

            <div className="text-center animate-fade-in-up animation-delay-500">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 hover:scale-110 transition-transform duration-200">
                <CreditCard className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">3. Instant Reimbursement</h3>
              <p className="text-green-100">
                Get reimbursed instantly in PYUSD when your expense is approved
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-display animate-fade-in-up">
            Ready to simplify your crypto expense management?
          </h2>
          <p className="text-xl text-gray-300 mb-8 animate-fade-in-up animation-delay-200">
            Join forward-thinking companies already using Expenso to streamline their crypto operations
          </p>
          <div className="flex justify-center animate-fade-in-up animation-delay-400">
            <button
              onClick={() => navigate('/login')}
              className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-3 flex items-center justify-center space-x-2 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
            >
              <span>Go to App</span>
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">
            Made with <span className="text-red-500">♥</span> by{' '}
            <a 
              href="https://github.com/akp111" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-green-400 hover:text-green-300 transition-colors duration-200"
            >
              akp111
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
